import React, { useState, useEffect, useMemo } from 'react';
import {
  WizardStep,
  ValidatedOptions,
  TextInput,
  Select,
  SelectOption,
  SelectList,
  MenuToggle,
  MenuToggleElement,
  Switch,
  Tooltip,
  Alert,
} from '@patternfly/react-core';
import { InfoCircleIcon } from '@patternfly/react-icons';
import { useFeatureFlags } from '@app/hooks/useFeatureFlags';
import { apiQuotaCheck, apiFetchTeams, apiFetchUserTeams, apiValidateDeployBranch } from '../helpers/apiHelpers';
import { useDebounce } from '../hooks/useDebounce';
import { useStableAccessToken } from '../hooks/useStableAccessToken';
import { useBudgetInfo } from '../hooks/useBudgetInfo';
import { isProdLikeCluster, isDevCluster, isQACluster, shouldEnforceTeamQuota } from '../helpers/clusterConfig';

import { ChooseBusinessUnitsStepProps } from './types';

export const ChooseBusinessUnitsStep: React.FC<ChooseBusinessUnitsStepProps> = ({
  businessData,
  selectedBusinessUnits,
  showWhitelistWarning,
  experiences,
  handleBusinessUnitSelect,
  applicationBranch,
  deploymentBranch,
  applicationBranchValidated,
  deploymentBranchValidated,
  setDeploymentBranchValidated,
  handleApplicationBranchChange,
  handleDeploymentBranchChange,
  handleNamespaceChange,
  namespaceValidated,
  errorMessage,
  granularDeployment,
  setGranularDeployment,
  location,
  setLocation,
  buildRailsEnv,
  setBuildRailsEnv,
  standalone,
  setStandalone,
  team,
  setTeam,
  envClass,
  setEnvClass,
  namespace,
  setNamespace,
  lastBuiltImage,
  setLastBuiltImage,
  extraEnvVars,
  setExtraEnvVars,
  enableNewRelic,
  setEnableNewRelic,
  downmergeMaster,
  setDownmergeMaster,
  enableRegressionProfile,
  setEnableRegressionProfile,
  authError,
  loadingExperiences,
  onQuotaChange,
  onBudgetBlockedChange,
  skipQuota,
  setSkipQuota,
  classError,
  setClassError,
  setDeployDisabled,
  deployRepoParams
}) => {

  const [tempBranchInput, setTempBranchInput] = useState(applicationBranch);
  const [namespaceManuallyEdited, setNamespaceManuallyEdited] = useState(false);
  // State to store quota warning message
  const [quotaWarning, setQuotaWarning] = useState<string>('');
  const [bannerVariant, setBannerVariant] = useState<'blue' | 'red'>('blue');
  const [clusterCapacityExceeded, setClusterCapacityExceeded] = useState(false);
  // Local states for controlling Select dropdown open/close (Advanced Options only)
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isBuildRailsEnvOpen, setIsBuildRailsEnvOpen] = useState(false);
  const [isStandaloneOpen, setIsStandaloneOpen] = useState(false);
  const [isClassOpen, setIsClassOpen] = useState(false);
  const [isTeamOpen, setIsTeamOpen] = useState(false);
  const [teams, setTeams] = useState<string[]>([]);
  const [baseTeam, setBaseTeam] = useState(team || '');
  const [overrideTeams, setOverrideTeams] = useState<string[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  // True when the Cortex call itself failed (as opposed to the user having no team).
  const [teamsLoadFailed, setTeamsLoadFailed] = useState(false);
  // True when the user has no Cortex team and the server assigned the default team.
  const [usingDefaultTeam, setUsingDefaultTeam] = useState(false);
  const [loadingOverrideTeams, setLoadingOverrideTeams] = useState(false);
  const [deployOnBehalfOfOtherTeam, setDeployOnBehalfOfOtherTeam] = useState(false);
  // With EOD budget enforcement on, deploying for another team is admin-only (spend stays on the user's own team).
  const { flags } = useFeatureFlags();
  // Decided server-side (flag, audience, admin role); shown until the flags have loaded, the server enforces it either way.
  const canDeployOnBehalf = flags.can_deploy_on_behalf ? flags.can_deploy_on_behalf.enabled : true;
  const [overrideTeam, setOverrideTeam] = useState('');
  const [isOverrideTeamOpen, setIsOverrideTeamOpen] = useState(false);
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const [deployBranchError, setDeployBranchError] = useState<string|null>(null)
  const [isEnableNewRelicOpen, setIsEnableNewRelicOpen] = useState(false);
  const [isLastBuiltImageOpen, setIsLastBuiltImageOpen] = useState(false);
  const [isEnableRegressionProfileOpen, setIsEnableRegressionProfileOpen] = useState(false);
  const [isDownmergeMasterOpen, setIsDownmergeMasterOpen] = useState(true);
  const [extraEnvVarsString, setExtraEnvVarsString] = React.useState(
    extraEnvVars.join(', ') // e.g. if parent's array is [KEY1=VAL1, KEY2=VAL2], we create "KEY1=VAL1, KEY2=VAL2"
  );

  const tokenGetter = useStableAccessToken();

   // 2) Debounce team & namespace for quota checks
   const debouncedTeam = useDebounce(team, 500);
   const debouncedNamespace = useDebounce(namespace, 500);

   const isProdLike = isProdLikeCluster();
   const isDevClusterFlag = isDevCluster();
   const isQAClusterFlag = isQACluster();

  const { budgetInfo, budgetBlocked, budgetBannerVariant, budgetBannerMessage, budgetCacheSubtext } = useBudgetInfo({
    team,
    debouncedTeam,
    debouncedNamespace,
    isProdLike,
    deployOnBehalfOfOtherTeam,
    overrideTeam,
  });

  useEffect(() => {
    onBudgetBlockedChange?.(budgetBlocked);
  }, [budgetBlocked, onBudgetBlockedChange]);

  useEffect(()=>{
    setTempBranchInput(applicationBranch)
  },[applicationBranch])

  useEffect(() => {
    if (isProdLike) {
      onQuotaChange(Infinity);
    }
  }, [isProdLike, onQuotaChange]);

  useEffect(() => {
    if (isQAClusterFlag && enableRegressionProfile) {
      setEnableRegressionProfile(false);
    }
  }, [isQAClusterFlag, enableRegressionProfile, setEnableRegressionProfile]);

  useEffect(() => {
    if (!deployOnBehalfOfOtherTeam) {
      setBaseTeam(team);
    }
  }, [team, deployOnBehalfOfOtherTeam]);
  
  // Fetch primary teams from Cortex when cluster changes
  useEffect(() => {
    if (isProdLike) {
      setTeams([]);
      setLoadingTeams(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingTeams(true);
      try {
        const { teams: list, usingDefaultTeam: assignedDefault } = await apiFetchUserTeams(tokenGetter);
        if (!cancelled) {
          setTeamsLoadFailed(false);
          setUsingDefaultTeam(assignedDefault);
          // Sort deprecated teams to the end so users are guided toward active teams
          const sorted = [
            ...list.filter(t => !/^\[deprecated\]/i.test(t)),
            ...list.filter(t => /^\[deprecated\]/i.test(t)),
          ];
          setTeams(sorted);
          const firstActive = sorted.find(t => !/^\[deprecated\]/i.test(t));
          if (sorted.length > 0 && (!team || team === 'Other' || !sorted.includes(team))) {
            setTeam(firstActive || sorted[0]);
          } else if (sorted.length === 0) {
            setTeam('');
          } else if (team && !sorted.includes(team)) {
            setTeam('');
          } else if (team && /^\[deprecated\]/i.test(team)) {
            // Current team is deprecated — auto-select the first active team
            setTeam(firstActive || sorted[0]);
          }
        }
      } catch (e) {
        console.error('Failed to load teams from Cortex:', e);
        if (!cancelled) {
          setTeams([]);
          setTeamsLoadFailed(true);
        }
      } finally {
        if (!cancelled) setLoadingTeams(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalThis.cluster_selected_value]);

  const cortexTeamNotFound = !loadingTeams && teams.length === 0 && !isProdLike;

  useEffect(() => {
    if (!deployOnBehalfOfOtherTeam || isProdLike) return;

    let cancelled = false;
    (async () => {
      setLoadingOverrideTeams(true);
      try {
        const list = await apiFetchTeams(tokenGetter, true);
        if (!cancelled) {
          setOverrideTeams(list.filter(t => !/^\[deprecated\]/i.test(t)));
        }
      } catch (e) {
        console.error('Failed to load override teams from Cortex:', e);
        if (!cancelled) setOverrideTeams([]);
      } finally {
        if (!cancelled) setLoadingOverrideTeams(false);
      }
    })();

    return () => { cancelled = true; };
  }, [deployOnBehalfOfOtherTeam, isProdLike, tokenGetter]);

  const handleDeployBranchBlur = async () => {
    // clear any existing error
    setDeployBranchError(null);
    setDeploymentBranchValidated(ValidatedOptions.default);
  
    try {
      const { exists, behindBy } = await apiValidateDeployBranch(deploymentBranch, tokenGetter);
  
      if (!exists) {
        setDeployBranchError(`Branch "${deploymentBranch}" does not exist`);
        setDeploymentBranchValidated(ValidatedOptions.error);
      } else if ((behindBy || 0) > 0) {
        setDeployBranchError(
          `Branch is behind master by ${behindBy ?? 0} commit${(behindBy ?? 0) > 1 ? 's' : ''}`
        );
        setDeploymentBranchValidated(ValidatedOptions.error);
      } else {
        setDeployBranchError(null);
        setDeploymentBranchValidated(ValidatedOptions.success);
      }
    } catch {
      setDeployBranchError('Could not validate deployment branch.');
      setDeploymentBranchValidated(ValidatedOptions.error);
    }
  };
  /**
   * 5) Debounce for team & namespace to do a quota check
   */
  useEffect(() => {
    if (isProdLike && debouncedNamespace) {
      // Prod-like clusters (preprod, sales) don't have a team-quota ConfigMap,
      // so /api/quota-check will 500 for non-running namespaces.
      // However it short-circuits BEFORE touching the ConfigMap when the namespace
      // is already running (returns { skipped: true }), so we can still use it to
      // detect running namespaces and enable standalone mode.
      // Any non-skipped result (including 500 errors) is treated as "not running".
      apiQuotaCheck('', debouncedNamespace, tokenGetter)
        .then((result) => {
          if (result.skipped) {
            setSkipQuota(true);
            onQuotaChange(Infinity);
            setBannerVariant('blue');
            setQuotaWarning(result.message || 'Namespace already running; skipping quota checks.');
            if (result.team) setTeam(result.team);
          } else {
            // Namespace not running (or ConfigMap missing on this cluster) — clear state
            setSkipQuota(false);
            setQuotaWarning('');
            setBannerVariant('blue');
            setClusterCapacityExceeded(false);
            onQuotaChange(Infinity);
          }
        })
        .catch(() => {
          // Network or unexpected error — treat as not running, no error shown
          setSkipQuota(false);
          setQuotaWarning('');
          setBannerVariant('blue');
          setClusterCapacityExceeded(false);
          onQuotaChange(Infinity);
        });
      return;
    }

    if (!isProdLike && debouncedTeam && debouncedNamespace) {
      apiQuotaCheck(debouncedTeam, debouncedNamespace, tokenGetter)
        .then((result) => {
          const { error, skipped, message, team: detectedTeam } = result;
          const msg = error || message || '';
          const isClusterCapacityError = !!(error && error.includes('Cluster has reached its maximum number of running namespaces'));
          setClusterCapacityExceeded(isClusterCapacityError);

          if (isClusterCapacityError) {
            onQuotaChange(0);
            setQuotaWarning('');
            setBannerVariant('red');
            setSkipQuota(false);
          } else if (skipped) {
            setSkipQuota(true);
            onQuotaChange(Infinity);
            setBannerVariant('blue');
            setQuotaWarning(msg);
            if (detectedTeam) setTeam(detectedTeam); // auto-fill team
          } else {
            setSkipQuota(false);
            setQuotaWarning(msg);
            setBannerVariant(error ? 'red' : 'blue');
            if (detectedTeam) setTeam(detectedTeam);
            onQuotaChange(Infinity);
          }
        })
        .catch(() => {
          setQuotaWarning('Could not verify quota at this time.');
          setBannerVariant('red');
          setClusterCapacityExceeded(false);
          setSkipQuota(false);
          onQuotaChange(Infinity);
        });

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTeam, debouncedNamespace, isProdLike, onQuotaChange, setSkipQuota, setTeam]); // tokenGetter is stable, don't include in deps

  // Toggle functions for advanced options dropdowns
  const locationToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsLocationOpen(!isLocationOpen)}
      isExpanded={isLocationOpen}
      style={{ width: '200px' }}
    >
      {location}
    </MenuToggle>
  );

  const buildRailsEnvToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsBuildRailsEnvOpen(!isBuildRailsEnvOpen)}
      isExpanded={isBuildRailsEnvOpen}
      style={{ width: '200px' }}
    >
      {buildRailsEnv}
    </MenuToggle>
  );

  const standaloneToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsStandaloneOpen(!isStandaloneOpen)}
      isExpanded={isStandaloneOpen}
      style={{ width: '200px' }}
    >
      {standalone ? 'true' : 'false'}
    </MenuToggle>
  );

  const classToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsClassOpen(!isClassOpen)}
      isExpanded={isClassOpen}
      style={{ width: '200px' }}
    >
      {envClass}
    </MenuToggle>
  );

  const isTeamDisabled = deployOnBehalfOfOtherTeam || skipQuota;

  const teamToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsTeamOpen(!isTeamOpen)}
      isDisabled={isTeamDisabled}
      isExpanded={isTeamOpen}
      style={{ width: '240px' }}
    >
      {team}
    </MenuToggle>
  );

  const overrideTeamToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsOverrideTeamOpen(!isOverrideTeamOpen)}
      isExpanded={isOverrideTeamOpen}
      style={{ width: '240px' }}
    >
      {overrideTeam || 'Select Cortex team'}
    </MenuToggle>
  );


  const [expSearch, setExpSearch] = useState('');
  const filteredExperiences = useMemo(
    () => experiences
      .filter(exp => exp !== '*')
      .filter(exp => exp.toLowerCase().includes(expSearch.toLowerCase())),
    [experiences, expSearch]
  );


  return (
    <WizardStep name="Configure" id="menu-bu-step" >
      <div className="deploy-layout">
        {/* Left Column — Experience Selection + Namespace & Team */}
        <div className="deploy-form-main">
          {/* Experience Selection Section */}
          <div className="deploy-section">
            <div className="deploy-section-title">
              <span className="section-icon">📦</span>
              Experience Selection
            </div>

            <div className="deploy-form-group">
              <label>
                Feature Branch{' '}
                <Tooltip content="Branch for application images + application-metadata.yaml">
                  <span className="info-icon">?</span>
                </Tooltip>
              </label>
              <TextInput
                value={tempBranchInput}
                onChange={e => {
                  setTempBranchInput(e.currentTarget.value);
                  setNamespaceManuallyEdited(false);
                }}
                onBlur={() => {
                  handleApplicationBranchChange(tempBranchInput);
                  const lower = tempBranchInput.toLowerCase();
                  if (!namespaceManuallyEdited && !['master', 'main', 'integration'].includes(lower)) {
                    handleNamespaceChange(lower);
                  }
                  setNamespaceManuallyEdited(true);
                }}
                aria-label="Application Branch Input"
                placeholder="Enter Application Branch"
                validated={applicationBranchValidated}
              />
              {applicationBranchValidated === ValidatedOptions.error && (
                <div className="dx-helper-text dx-helper-text--error">Invalid branch name</div>
              )}
            </div>

            <div className="deploy-form-group">
              <label>
                Select Experience{' '}
                <Tooltip
                  position="right"
                  content={
                    <div style={{ maxWidth: '200px', fontSize: '0.9rem' }}>
                      Experience is a group of applications that belong to the same business unit.{' '}
                      <a href="https://euewizard.docs.teladoc.dev/#/pages/dev?id=-i-want-to-onboard-my-repo-with-application-metadata-files-dependency-graph" target="_blank" rel="noopener noreferrer">Learn more</a>
                    </div>
                  }
                >
                  <span className="info-icon">?</span>
                </Tooltip>
              </label>
              {showWhitelistWarning && (
                <div className="deploy-notice deploy-notice--info">
                  <span className="deploy-notice-icon">ℹ</span>
                  <span>
                    <strong>Protected branches</strong> show only <strong>whitelisted experiences</strong>.
                    {' '}<strong>Feature branches</strong> show all experiences.{' '}
                    <a href="https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/303727044/Adding+Experiences+to+the+Default+Branches+Whitelist+Runbook" target="_blank" rel="noopener noreferrer">ConfigMap runbook</a>
                  </span>
                </div>
              )}
              <div className="exp-list-container">
                <div className="exp-search-wrapper">
                  <span className="exp-search-icon">🔍</span>
                  <input
                    className="exp-search-input"
                    type="text"
                    placeholder="Search experiences..."
                    value={expSearch}
                    onChange={e => setExpSearch(e.target.value)}
                  />
                </div>
                <div className="exp-list-items" role="listbox" aria-label="Experiences" aria-multiselectable="true">
                  {loadingExperiences ? (
                    <div className="dx-flex" style={{ padding: '16px', justifyContent: 'center' }}>
                      <div className="dx-spinner dx-spinner--sm" aria-label="Loading experiences" />
                    </div>
                  ) : filteredExperiences.map(exp => {
                    const isSelected = selectedBusinessUnits.includes(exp);
                    return (
                      <div
                        key={exp}
                        className={`exp-list-item${isSelected ? ' selected' : ''}`}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleBusinessUnitSelect(exp)}
                      >
                        <span className={`exp-list-checkbox${isSelected ? ' checked' : ''}`} />
                        {exp}
                      </div>
                    );
                  })}
                </div>
              </div>
              {selectedBusinessUnits.length > 0 && (
                <div className="selected-count">{selectedBusinessUnits.length} experience{selectedBusinessUnits.length !== 1 ? 's' : ''} selected</div>
              )}
            </div>

            <div style={{ marginTop: '8px' }}>
              <Tooltip content="Toggle on to select specific applications and branches for deployment">
                <Switch
                  id="granular-deployment-switch"
                  label="Custom Selections"
                  isChecked={granularDeployment}
                  onChange={(_event, checked) => setGranularDeployment(checked)}
                  ouiaId="GranularDeploymentSwitch"
                />
              </Tooltip>
            </div>
            {errorMessage && <div className="dx-helper-text dx-helper-text--error" style={{ marginTop: '8px' }}>{errorMessage}</div>}
          </div>

          {/* Namespace & Team Section */}
          <div className="deploy-section" style={{ marginTop: '16px' }}>
            <div className="deploy-section-title">
              <span className="section-icon">🏷️</span>
              Namespace & Team
            </div>
            <div className="deploy-form-row">
              <div className="deploy-form-group">
                <label>
                  Namespace{' '}
                  <Tooltip content="Target namespace for deployment">
                    <span className="info-icon">?</span>
                  </Tooltip>
                </label>
                <TextInput
                  value={namespace}
                  type="text"
                  maxLength={40}
                  onChange={e => {
                    setNamespace(e.currentTarget.value);
                    setNamespaceManuallyEdited(true);
                  }}
                  onBlur={() => handleNamespaceChange(namespace)}
                  aria-label="Namespace Input"
                  placeholder="e.g., qa-default"
                  validated={namespaceValidated}
                />
                {namespaceValidated === ValidatedOptions.error && (
                  <div className="dx-helper-text dx-helper-text--error">Invalid namespace</div>
                )}
              </div>
              {!isProdLike && (
                <div className="deploy-form-group">
                  <label>
                    Team{' '}
                    <Tooltip content="Team responsible for this environment">
                      <span className="info-icon">?</span>
                    </Tooltip>
                  </label>
                  <Select
                    id="team-select"
                    isOpen={isTeamOpen}
                    selected={team}
                    toggle={teamToggle}
                    onSelect={(_, sel) => {
                      setTeam(sel as string);
                      if (!deployOnBehalfOfOtherTeam) {
                        setBaseTeam(sel as string);
                      }
                      setIsTeamOpen(false);
                    }}
                    onOpenChange={setIsTeamOpen}
                    shouldFocusToggleOnSelect
                  >
                    <SelectList style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {loadingTeams
                        ? <div className="dx-spinner dx-spinner--sm" aria-label="Loading teams…" />
                        : teams.map(t => (
                            <SelectOption key={t} value={t}>{t}</SelectOption>
                          ))
                      }
                    </SelectList>
                  </Select>
                  {canDeployOnBehalf && (
                  <div style={{ marginTop: '8px' }}>
                    <Tooltip content="Enable this option to deploy on behalf of another team if your team's quota has run out">
                      <Switch
                        id="deploy-on-behalf-switch"
                        label="Deploy on behalf of another team"
                        isChecked={deployOnBehalfOfOtherTeam}
                        onChange={(_event, checked) => {
                          setDeployOnBehalfOfOtherTeam(checked);
                          setQuotaWarning('');
                          setOverrideTeam('');
                          setIsOverrideTeamOpen(false);
                          if (checked) {
                            setBaseTeam(team);
                            setTeam(team);
                          } else {
                            setTeam(baseTeam || teams[0] || '');
                          }
                        }}
                      />
                    </Tooltip>
                  </div>
                  )}

                  {deployOnBehalfOfOtherTeam && (
                    <div style={{ marginTop: '8px' }}>
                      <label>Deploy as another team</label>
                      <Select
                        id="override-team-select"
                        isOpen={isOverrideTeamOpen}
                        selected={overrideTeam || undefined}
                        toggle={overrideTeamToggle}
                        onSelect={(_, sel) => {
                          setOverrideTeam(sel as string);
                          setTeam(sel as string);
                          setIsOverrideTeamOpen(false);
                        }}
                        onOpenChange={setIsOverrideTeamOpen}
                        shouldFocusToggleOnSelect
                      >
                        <SelectList style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {loadingOverrideTeams
                            ? <div className="dx-spinner dx-spinner--sm" aria-label="Loading Cortex teams..." />
                            : overrideTeams.map(t => (
                                <SelectOption key={t} value={t}>{t}</SelectOption>
                              ))
                          }
                        </SelectList>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
            {skipQuota && (
              <Alert variant="info" isInline title="This namespace is running, please select standalone apps/infra to deploy at step 2" style={{ fontSize: '0.85rem', marginTop: '8px' }} />
            )}
            {(applicationBranch === 'master' || applicationBranch === 'main' || applicationBranch === 'integration') && (
              <div className="dx-banner dx-banner--warning" style={{ marginTop: '8px' }}>
                <div className="dx-flex dx-flex--gap-sm">
                  <span><InfoCircleIcon /></span>
                  <span>Deploying from default branch; please specify a custom namespace.</span>
                </div>
              </div>
            )}
            {clusterCapacityExceeded && (
              <Alert
                variant="danger"
                isInline
                title="Cluster capacity exceeded"
                style={{ marginTop: '8px' }}
              >
                The cluster has reached its maximum number of running namespaces. Please bring down some running environments before deploying a new one.
              </Alert>
            )}
            {quotaWarning && !clusterCapacityExceeded && (
              <div className={`dx-banner dx-banner--${bannerVariant === 'red' ? 'danger' : 'info'}`} style={{ marginTop: '8px' }}>
                <div className="dx-flex dx-flex--gap-sm">
                  <span><InfoCircleIcon /></span>
                  <span>{quotaWarning}</span>
                </div>
              </div>
            )}
            {usingDefaultTeam && !isProdLike && (
              <div className="dx-banner dx-banner--info" style={{ marginTop: '8px' }}>
                <div className="dx-flex dx-flex--gap-sm">
                  <span><InfoCircleIcon /></span>
                  <span>
                    We couldn&apos;t find a Cortex team for you, so this environment will be tracked under the default team <strong>{team}</strong>.{' '}
                    <a
                      href="https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/374178165/Cortex+User+Guide+Overview"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn how to register your team in Cortex
                    </a>
                  </span>
                </div>
              </div>
            )}
            {teamsLoadFailed && !isProdLike ? (
              <div className="dx-banner dx-banner--warning" style={{ marginTop: '8px' }}>
                <div className="dx-flex dx-flex--gap-sm">
                  <span><InfoCircleIcon /></span>
                  <span>
                    We couldn&apos;t load your teams from Cortex right now, so your team budget can&apos;t be checked. You can continue.
                  </span>
                </div>
              </div>
            ) : cortexTeamNotFound && !deployOnBehalfOfOtherTeam ? (
              <div className="dx-banner dx-banner--info" style={{ marginTop: '8px' }}>
                <div className="dx-flex dx-flex--gap-sm">
                  <span><InfoCircleIcon /></span>
                  <span>
                    Your Cortex team could not be identified, so your budget cannot be calculated.
                    {canDeployOnBehalf
                      ? <> Please enable <strong>Deploy on behalf of another team</strong> above and select your team manually.</>
                      : ' Deploying is unavailable until your team is found. If your team is already registered, try again in a few minutes.'}{' '}
                    <a
                      href="https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/374178165/Cortex+User+Guide+Overview"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn how to register your team in Cortex
                    </a>
                  </span>
                </div>
              </div>
            ) : (
              budgetInfo && (
                <div className={`dx-banner dx-banner--${budgetBannerVariant}`} style={{ marginTop: '8px' }}>
                  <div className="dx-flex dx-flex--gap-sm">
                    <span><InfoCircleIcon /></span>
                    <span>
                      <span>{budgetBannerMessage}</span>
                      {budgetBlocked && budgetInfo.formUrl && (
                        <span style={{ display: 'block', marginTop: '2px' }}>
                          <a href={budgetInfo.formUrl} target="_blank" rel="noopener noreferrer">Request an exception</a>
                        </span>
                      )}
                      {budgetCacheSubtext && (
                        <span style={{ display: 'block', fontSize: '0.8em', opacity: 0.7, marginTop: '2px' }}>
                          {budgetCacheSubtext}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )
            )}
            {authError && (
              <Alert variant="danger" title={authError} style={{ marginTop: '8px' }} />
            )}
          </div>
        </div>

        {/* Right Column — Branch Configuration + Advanced Options */}
        <div className="deploy-form-sidebar">
          {/* Branch Configuration Card */}
          <div className="deploy-section">
            <div className="deploy-section-title">
              <span className="section-icon">🌿</span>
              Branch Configuration
            </div>
            <div className="deploy-form-group">
              <label>
                Medulla Branch{' '}
                <Tooltip content="Which helmfiles version to deploy from Teladoc/medulla">
                  <span className="info-icon">?</span>
                </Tooltip>
              </label>
              <TextInput
                value={deploymentBranch}
                onChange={(_e, val) => {
                  handleDeploymentBranchChange(val);
                  setDeployBranchError(null);
                  setDeploymentBranchValidated(ValidatedOptions.default);
                }}
                onBlur={handleDeployBranchBlur}
                placeholder="e.g., main"
                validated={deploymentBranchValidated}
              />
              {deployBranchError && (
                <div className="dx-helper-text dx-helper-text--error">{deployBranchError}</div>
              )}
            </div>
            <div className="deploy-form-group">
              <label>
                Deploy Repo Branch{' '}
                <Tooltip content="Deploy config repo branch from Teladoc/deploy">
                  <span className="info-icon">?</span>
                </Tooltip>
              </label>
              <TextInput
                value={deployRepoParams.value}
                onChange={(_, value) => deployRepoParams.onChange(value)}
                onBlur={deployRepoParams.onBlur}
                placeholder="e.g., main"
                validated={deployRepoParams.valid}
              />
              {deployRepoParams.valid !== 'success' && deployRepoParams.valid !== 'default' && (
                <div className={`dx-helper-text dx-helper-text--${deployRepoParams.valid === 'error' ? 'error' : 'warning'}`}>
                  {deployRepoParams.message}
                </div>
              )}
            </div>
          </div>

          {/* Advanced Options (Collapsible) */}
          <div className={`deploy-section collapsible ${isAdvancedOptionsOpen ? '' : 'collapsed'}`}>
            <div className="deploy-section-title collapsible-header" onClick={() => setIsAdvancedOptionsOpen(!isAdvancedOptionsOpen)}>
              <span className="section-icon">⚙️</span>
              Advanced Options
              <span className="collapse-icon" style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)', transition: 'transform 0.2s' }}>▶</span>
            </div>
            <div className="collapsible-content">
              <div className="deploy-form-group">
                <label>LOCATION</label>
                <Select id="location-select" isOpen={isLocationOpen} selected={location}
                  onSelect={(_e, selection) => { setLocation(selection as string); setIsLocationOpen(false); }}
                  onOpenChange={setIsLocationOpen} toggle={locationToggle} aria-label="Select location">
                  <SelectOption value="USA">USA</SelectOption>
                  <SelectOption value="USA-DR">USA-DR</SelectOption>
                  <SelectOption value="CAN">CAN</SelectOption>
                  <SelectOption value="CAN-DR">CAN-DR</SelectOption>
                  <SelectOption value="EU">EU</SelectOption>
                  <SelectOption value="EU-DR">EU-DR</SelectOption>
                </Select>
              </div>
              <div className="deploy-form-group">
                <label>STANDALONE</label>
                <Select id="standalone-select" isOpen={isStandaloneOpen} selected={standalone ? 'true' : 'false'}
                  onSelect={(_event, selection) => { setStandalone(selection === 'true'); setIsStandaloneOpen(false); }}
                  onOpenChange={setIsStandaloneOpen} toggle={standaloneToggle} shouldFocusToggleOnSelect>
                  <SelectList>
                    <SelectOption value="true">true</SelectOption>
                    <SelectOption value="false">false</SelectOption>
                  </SelectList>
                </Select>
              </div>
              <div className="deploy-form-group">
                <label>CLASS</label>
                <Select id="class-select" isOpen={isClassOpen} selected={envClass}
                  onSelect={(_e, selection) => {
                    const val = String(selection);
                    if (isDevClusterFlag && val === 'persistent') {
                      setClassError?.('Persistent class is not allowed on the dev cluster.');
                      setDeployDisabled?.(true);
                      setEnvClass('ephemeral');
                    } else {
                      setClassError?.(null);
                      setDeployDisabled?.(false);
                      setEnvClass(val);
                    }
                    setIsClassOpen(false);
                  }}
                  onOpenChange={setIsClassOpen} toggle={classToggle} shouldFocusToggleOnSelect>
                  <SelectList>
                    <SelectOption value="ephemeral">ephemeral</SelectOption>
                    <SelectOption value="persistent" isDisabled={isDevClusterFlag}>persistent</SelectOption>
                  </SelectList>
                </Select>
                {isDevClusterFlag && (
                  <div className={`deploy-notice ${classError ? 'deploy-notice--danger' : 'deploy-notice--info'}`} style={{ marginTop: 6 }}>
                    <span className="deploy-notice-icon">ℹ</span>
                    <span>{classError || 'Dev cluster supports only ephemeral environments.'}</span>
                  </div>
                )}
              </div>
              <div className="deploy-form-group">
                <label>BUILD_RAILS_ENV</label>
                <Select id="build-rails-env-select" isOpen={isBuildRailsEnvOpen} selected={buildRailsEnv}
                  onSelect={(_event, selection) => { setBuildRailsEnv(selection as string); setIsBuildRailsEnvOpen(false); }}
                  onOpenChange={setIsBuildRailsEnvOpen} toggle={buildRailsEnvToggle} shouldFocusToggleOnSelect>
                  <SelectList>
                    <SelectOption value="production">production</SelectOption>
                    <SelectOption value="staging">staging</SelectOption>
                  </SelectList>
                </Select>
              </div>
              <div className="deploy-form-group">
                <label>ENABLE_NEW_RELIC</label>
                <Select id="enable-new-relic-select" isOpen={isEnableNewRelicOpen} selected={enableNewRelic ? 'true' : 'false'}
                  toggle={(toggleRef) => (<MenuToggle ref={toggleRef} onClick={() => setIsEnableNewRelicOpen(!isEnableNewRelicOpen)} isExpanded={isEnableNewRelicOpen} style={{ width: '100%' }}>{enableNewRelic ? 'true' : 'false'}</MenuToggle>)}
                  onSelect={(_e, value) => { setEnableNewRelic(value === 'true'); setIsEnableNewRelicOpen(false); }}
                  onOpenChange={setIsEnableNewRelicOpen} shouldFocusToggleOnSelect>
                  <SelectList>
                    <SelectOption value="true">true</SelectOption>
                    <SelectOption value="false">false</SelectOption>
                  </SelectList>
                </Select>
              </div>
              <div className="deploy-form-group">
                <label>ENABLE_REGRESSION_PROFILE</label>
                <Select id="enable-regression-profile-select" isOpen={isEnableRegressionProfileOpen} selected={enableRegressionProfile ? 'true' : 'false'}
                  toggle={(toggleRef) => (<MenuToggle ref={toggleRef} onClick={() => setIsEnableRegressionProfileOpen(!isEnableRegressionProfileOpen)} isExpanded={isEnableRegressionProfileOpen} isDisabled={isQAClusterFlag} style={{ width: '100%' }}>{enableRegressionProfile ? 'true' : 'false'}</MenuToggle>)}
                  onSelect={(_e, value) => { setEnableRegressionProfile(value === 'true'); setIsEnableRegressionProfileOpen(false); }}
                  onOpenChange={setIsEnableRegressionProfileOpen} shouldFocusToggleOnSelect>
                  <SelectList>
                    <SelectOption value="true">true</SelectOption>
                    <SelectOption value="false">false</SelectOption>
                  </SelectList>
                </Select>
                {isQAClusterFlag && (
                  <div className="deploy-notice deploy-notice--info" style={{ marginTop: 6 }}>
                    <span className="deploy-notice-icon">ℹ</span>
                    <span>ENABLE_REGRESSION_PROFILE is not available on the QA cluster.</span>
                  </div>
                )}
              </div>
              <div className="deploy-form-group">
                <label>LAST_BUILT_IMAGE</label>
                <Select id="last-built-image-select" isOpen={isLastBuiltImageOpen} selected={lastBuiltImage ? 'true' : 'false'}
                  toggle={(toggleRef) => (<MenuToggle ref={toggleRef} onClick={() => setIsLastBuiltImageOpen(!isLastBuiltImageOpen)} isExpanded={isLastBuiltImageOpen} style={{ width: '100%' }}>{lastBuiltImage ? 'true' : 'false'}</MenuToggle>)}
                  onSelect={(_e, value) => { setLastBuiltImage(value === 'true'); setIsLastBuiltImageOpen(false); }}
                  onOpenChange={setIsLastBuiltImageOpen} shouldFocusToggleOnSelect>
                  <SelectList>
                    <SelectOption value="true">true</SelectOption>
                    <SelectOption value="false">false</SelectOption>
                  </SelectList>
                </Select>
              </div>
              <div className="deploy-form-group">
                <label>UPDATE_DEPLOY_REPO</label>
                <Select id="downmerge-master-select" isOpen={isDownmergeMasterOpen} selected={downmergeMaster ? 'true' : 'false'}
                  toggle={(toggleRef) => (<MenuToggle ref={toggleRef} onClick={() => setIsDownmergeMasterOpen(!isDownmergeMasterOpen)} isExpanded={isDownmergeMasterOpen} style={{ width: '100%' }}>{downmergeMaster ? 'true' : 'false'}</MenuToggle>)}
                  onSelect={(_e, value) => { setDownmergeMaster(value === 'true'); setIsDownmergeMasterOpen(false); }}
                  onOpenChange={setIsDownmergeMasterOpen} shouldFocusToggleOnSelect>
                  <SelectList>
                    <SelectOption value="true">true</SelectOption>
                    <SelectOption value="false">false</SelectOption>
                  </SelectList>
                </Select>
                <small style={{ marginTop: '4px', color: 'var(--text-muted)', fontStyle: 'italic', display: 'block' }}>
                  DEPLOY REPO branch will be updated with the master branch.
                </small>
              </div>
              <div className="deploy-form-group">
                <label>EXTRA ENV VARIABLES</label>
                <TextInput
                  id='extra-env-vars-input'
                  value={extraEnvVarsString}
                  onChange={e => {
                    const newStr = e.currentTarget.value;
                    setExtraEnvVarsString(newStr);
                    const arr = newStr.split(",").map(s => s.trim()).filter(Boolean);
                    setExtraEnvVars(arr);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </WizardStep>
  );
};
